import { useQuery } from '@tanstack/react-query';
import { generatePath } from 'react-router-dom';
import { testsApi } from '@/api/tests';
import { ArrowRight } from '@/components/icons';
import { Button } from '@/components/ui/button';
import Loading from '@/components/ui/Loading';
import { useModalContext } from '@/components/ui/modal-provider';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { PatientDetailPage, TestResponsePage } from '@/pages/constants';
import { getOutcomeConfig } from '@/pages/tests/outcomes/registry';

type Props = {
    patientId: string;
    assignmentId: string;
};

const formatValue = (value: unknown) => {
    if (value === null || value === undefined || value === '') return '--';
    if (typeof value === 'number') return value.toString();
    return String(value);
};

export default function TestAnswersModal({ patientId, assignmentId }: Props) {
    const { closeModal } = useModalContext();
    const { data, isLoading, error } = useQuery({
        queryKey: ['patient-test-answers', patientId, assignmentId],
        queryFn: () => testsApi.getAssignmentAnswers(patientId, assignmentId),
        enabled: Boolean(patientId && assignmentId),
        retry: false,
    });

    const handleOpenFullAnswers = () => {
        closeModal();
        const returnTo = `/${generatePath(PatientDetailPage, { patientId })}`;
        const target = `/${generatePath(TestResponsePage, {
            patientId,
            assignmentId,
        })}?returnTo=${encodeURIComponent(returnTo)}`;
        window.location.assign(target);
    };

    if (isLoading) return <Loading />;

    const fullAnswersButton = (
        <Button variant="outline" size="sm" onClick={handleOpenFullAnswers} className="gap-2">
            Ver respostas completas
            <ArrowRight className="size-4" />
        </Button>
    );

    if (error) {
        return (
            <div className="space-y-4">
                <div className="text-sm text-muted-foreground">
                    Não foi possível carregar os resultados deste instrumento.
                </div>
                <div className="flex justify-end">{fullAnswersButton}</div>
            </div>
        );
    }

    const outcomes = data?.outcomes || null;
    const config = getOutcomeConfig(data?.test_identifier, outcomes || undefined);

    if (!outcomes) {
        return (
            <div className="space-y-4">
                <div className="text-sm text-muted-foreground">
                    Resultados ainda não disponíveis para este instrumento.
                </div>
                <div className="flex justify-end">{fullAnswersButton}</div>
            </div>
        );
    }

    if (!config) {
        return (
            <div className="space-y-4">
                <div className="text-sm text-muted-foreground">
                    A apresentação dos resultados deste instrumento ainda não foi configurada.
                </div>
                <div className="flex justify-end">{fullAnswersButton}</div>
            </div>
        );
    }

    const rows = config.getRows(outcomes);

    if (rows.length === 0) {
        return (
            <div className="space-y-4">
                <div className="text-sm text-muted-foreground">
                    Nenhum resultado foi encontrado para este instrumento.
                </div>
                <div className="flex justify-end">{fullAnswersButton}</div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {config.title}
                </p>
                <p className="text-sm text-muted-foreground">
                    Resultados calculados a partir das respostas registradas.
                </p>
            </div>

            <div className="flex justify-end">{fullAnswersButton}</div>

            <div className="overflow-hidden rounded-lg border">
                <Table>
                    <TableHeader className="border-b bg-surface">
                        <TableRow>
                            {config.columns.map((column) => (
                                <TableHead key={column.key} className="p-3 text-sm">
                                    {column.label}
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.map((row, index) => (
                            <TableRow key={`${row[config.columns[0].key] ?? 'row'}-${index}`}>
                                {config.columns.map((column) => {
                                    const value = row[column.key];
                                    const formatted = column.format
                                        ? column.format(value)
                                        : formatValue(value);
                                    return (
                                        <TableCell key={column.key} className="p-3 text-sm">
                                            {formatted}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
